<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import aiConfigAPI from 'dashboard/api/aiConfig';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Btn from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const showToast = useAlert;

const PROVIDERS = ['openai', 'openrouter', 'groq', 'nvidia', 'gemini'];

const config = ref({ defaultProvider: 'openai', providers: {} });
const saving = ref(false);
const loading = ref(true);
const showAddForm = ref(false);
const newProviderName = ref('');
const customProviderName = ref('');

const modelsLists = reactive({});
const loadingModels = reactive({});
const modelsErrors = reactive({});
const manualMode = reactive({});

const DEFAULT_BASE_URLS = {
  openai: 'https://api.openai.com/v1',
  openrouter: 'https://openrouter.ai/api/v1',
  groq: 'https://api.groq.com/openai/v1',
  nvidia: 'https://integrate.api.nvidia.com/v1',
};

const providerOptions = computed(() => {
  const set = new Set([...PROVIDERS, ...Object.keys(config.value.providers)]);
  return Array.from(set).map(p => ({ value: p, label: p }));
});

const availableProviders = computed(() =>
  PROVIDERS.filter(p => !config.value.providers[p]).map(p => ({ value: p, label: p }))
);

const fetchModels = async (provider, apiKey, baseURL) => {
  const normalized = String(provider || '').toLowerCase();
  if (!apiKey) return;
  loadingModels[normalized] = true;
  modelsErrors[normalized] = null;
  try {
    const response = await aiConfigAPI.fetchModels({
      provider: normalized,
      apiKey: apiKey.trim(),
      baseURL: baseURL || '',
    });
    const fetched = response.data?.models || [];
    modelsLists[normalized] = fetched;
    // auto-seleciona o primeiro modelo apenas se ainda não houver modelo definido
    const current = config.value.providers[normalized]?.model;
    if (fetched.length > 0 && !current) {
      updateProvider(normalized, 'model', fetched[0]);
    }
  } catch (error) {
    modelsErrors[normalized] =
      error.response?.data?.error || 'Erro ao carregar modelos. Verifique sua API Key.';
  } finally {
    loadingModels[normalized] = false;
  }
};

const loadConfig = async () => {
  loading.value = true;
  try {
    const response = await aiConfigAPI.getConfig();
    if (response.data) {
      config.value = {
        defaultProvider: response.data.defaultProvider || 'openai',
        providers: response.data.providers || {},
      };
      Object.entries(config.value.providers).forEach(([provider, cfg]) => {
        if (cfg.apiKey) fetchModels(provider, cfg.apiKey, cfg.baseURL);
      });
    } else {
      config.value = { defaultProvider: 'openai', providers: {} };
    }
  } catch (error) {
    showToast('Erro ao carregar configuração', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(loadConfig);

const updateProvider = (provider, field, value) => {
  config.value = {
    ...config.value,
    providers: {
      ...config.value.providers,
      [provider]: {
        ...config.value.providers[provider],
        [field]: value,
      },
    },
  };
};

const saveConfig = async () => {
  saving.value = true;
  try {
    await aiConfigAPI.saveConfig({ config: config.value });
    showToast('Configuração salva com sucesso!');
  } catch (error) {
    showToast(error.response?.data?.error || 'Erro ao salvar configuração', 'error');
  } finally {
    saving.value = false;
  }
};

const confirmAddProvider = () => {
  const name =
    newProviderName.value === 'custom'
      ? customProviderName.value.trim().toLowerCase()
      : newProviderName.value;
  if (name && !config.value.providers[name]) {
    config.value = {
      ...config.value,
      providers: {
        ...config.value.providers,
        [name]: { apiKey: '', model: '' },
      },
    };
  }
  hideAddForm();
};

const hideAddForm = () => {
  showAddForm.value = false;
  newProviderName.value = '';
  customProviderName.value = '';
};

const removeProvider = provider => {
  const next = { ...config.value.providers };
  delete next[provider];
  config.value = { ...config.value, providers: next };

  delete modelsLists[provider];
  delete modelsErrors[provider];
  delete manualMode[provider];
  delete loadingModels[provider];
};

const toggleManualMode = provider => {
  manualMode[provider] = !manualMode[provider];
};

const onTemperature = (provider, value) => {
  const num = value === '' || value === null || value === undefined ? undefined : parseFloat(value);
  updateProvider(provider, 'temperature', num);
};
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-solid-1">
    <div class="mx-auto max-w-6xl px-6 py-6 flex flex-col gap-6">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <div class="flex items-center justify-center size-10 rounded-xl bg-n-brand text-white">
          <Icon name="sparkles" :size="20" />
        </div>
        <div>
          <h1 class="text-heading-1 text-n-slate-12">Configuração de IA</h1>
          <p class="text-description text-n-slate-11">
            Cadastre suas chaves de API para provedores de IA
          </p>
        </div>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-20">
        <Spinner />
      </div>

      <div v-else class="flex flex-col gap-6">
        <!-- Provedor Padrão -->
        <CardLayout class="p-5">
          <h3 class="text-heading-4 text-n-slate-12 mb-1">Provedor Padrão</h3>
          <p class="text-description text-n-slate-11 mb-4">Selecione o provedor padrão para uso geral</p>
          <div class="max-w-[320px]">
            <span class="text-caption text-n-slate-11 mb-1 block">Provedor Ativo</span>
            <Select v-model="config.defaultProvider" :options="providerOptions" />
          </div>
        </CardLayout>

        <!-- Provedores Configurados -->
        <CardLayout class="p-5">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-heading-4 text-n-slate-12 mb-1">Provedores Configurados</h3>
              <p class="text-description text-n-slate-11">Adicione e configure seus provedores de IA</p>
            </div>
            <Btn label="Adicionar Provedor" color="blue" @click="showAddForm = true">
              <template #prefix>
                <Icon name="plus" :size="16" />
              </template>
            </Btn>
          </div>

          <!-- Form adicionar -->
          <div
            v-if="showAddForm"
            class="mb-4 p-4 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
          >
            <h4 class="text-sm font-semibold text-n-slate-12 mb-3">Novo Provedor de IA</h4>
            <div class="flex flex-wrap gap-3 items-end">
              <div class="flex-1 min-w-[200px]">
                <span class="text-caption text-n-slate-11 mb-1 block">Selecionar Provedor</span>
                <Select
                  v-model="newProviderName"
                  :options="[{ value: '', label: '-- Selecione --' }, ...availableProviders, { value: 'custom', label: 'Outro (Customizado)...' }]"
                />
              </div>
              <div v-if="newProviderName === 'custom'" class="flex-1 min-w-[200px]">
                <Input v-model="customProviderName" label="Nome do Provedor" placeholder="Ex: anthropic, mistral" />
              </div>
              <div class="flex gap-2 min-w-[200px]">
                <Btn
                  label="Confirmar"
                  color="blue"
                  class="flex-1"
                  :disabled="!newProviderName || (newProviderName === 'custom' && !customProviderName.trim())"
                  @click="confirmAddProvider"
                />
                <Btn label="Cancelar" variant="outline" class="flex-1" @click="hideAddForm" />
              </div>
            </div>
          </div>

          <!-- Lista de cards -->
          <div v-if="Object.keys(config.providers).length" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div
              v-for="(cfg, provider) in config.providers"
              :key="provider"
              class="p-4 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container flex flex-col gap-4"
            >
              <!-- Card header -->
              <div class="flex items-center justify-between border-b border-n-container pb-2">
                <div class="flex items-center gap-2">
                  <span class="size-2 rounded-full bg-n-brand shadow-[0_0_6px_var(--color-brand)]" />
                  <span class="text-sm font-bold text-n-slate-12 capitalize">{{ provider }}</span>
                </div>
                <Btn variant="ghost" color="ruby" icon="trash-2" @click="removeProvider(provider)" />
              </div>

              <!-- API Key -->
              <div>
                <Input
                  v-model="cfg.apiKey"
                  type="password"
                  label="API Key"
                  :placeholder="`Insira sua API Key para ${provider}`"
                  @blur="fetchModels(provider, cfg.apiKey, cfg.baseURL)"
                />
              </div>

              <!-- Modelo -->
              <div>
                <div class="flex items-center justify-between mb-1">
                  <span class="text-caption text-n-slate-11">Modelo Padrão</span>
                  <div v-if="cfg.apiKey && !loadingModels[provider]" class="flex items-center gap-2">
                    <button
                      v-if="modelsLists[provider]?.length"
                      type="button"
                      class="text-[11px] text-n-brand hover:underline"
                      @click="toggleManualMode(provider)"
                    >
                      {{ manualMode[provider] ? 'Selecionar da lista' : 'Digitar manualmente' }}
                    </button>
                    <button
                      type="button"
                      class="text-[11px] text-n-brand hover:underline"
                      @click="fetchModels(provider, cfg.apiKey, cfg.baseURL)"
                    >
                      🔄 Buscar Modelos
                    </button>
                  </div>
                </div>

                <div v-if="loadingModels[provider]" class="flex items-center gap-2 py-2 text-[13px] text-n-slate-11">
                  <Spinner />
                  <span>Buscando modelos disponíveis...</span>
                </div>
                <Select
                  v-else-if="!manualMode[provider] && modelsLists[provider]?.length"
                  v-model="cfg.model"
                  :options="modelsLists[provider].map(m => ({ value: m, label: m }))"
                />
                <Input
                  v-else
                  v-model="cfg.model"
                  placeholder="Ex: gpt-4o, gemini-2.5-pro, etc."
                />
                <p v-if="modelsErrors[provider]" class="text-[11px] text-ruby-11 mt-1">
                  {{ modelsErrors[provider] }}
                </p>
              </div>

              <!-- Base URL + Temperatura -->
              <div class="flex gap-3">
                <div v-if="provider !== 'gemini'" class="flex-1">
                  <Input
                    v-model="cfg.baseURL"
                    label="Base URL (Opcional)"
                    :placeholder="DEFAULT_BASE_URLS[provider] || 'https://api.openai.com/v1'"
                    @blur="fetchModels(provider, cfg.apiKey, cfg.baseURL)"
                  />
                </div>
                <div :class="provider === 'gemini' ? 'flex-1' : 'flex-1'">
                  <Input
                    :model-value="cfg.temperature ?? ''"
                    type="number"
                    label="Temperatura"
                    placeholder="Ex: 0.7"
                    @update:model-value="onTemperature(provider, $event)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-else
            class="text-center py-10 border border-dashed border-n-container rounded-lg mt-4"
          >
            <p class="text-description text-n-slate-11">
              Nenhum provedor configurado. Clique em "Adicionar Provedor" para começar.
            </p>
          </div>
        </CardLayout>

        <!-- Salvar -->
        <div class="flex justify-end">
          <Btn :label="saving ? 'Salvando...' : 'Salvar Configuração'" color="blue" :disabled="saving" @click="saveConfig">
            <template #prefix>
              <Icon name="save" :size="16" />
            </template>
          </Btn>
        </div>

        <!-- Como usar -->
        <CardLayout class="p-5">
          <h4 class="text-[15px] font-semibold text-n-slate-12 mb-2 flex items-center gap-2">
            <Icon name="circle-help" :size="18" class="text-n-brand" />
            Como usar
          </h4>
          <p class="text-[13px] text-n-slate-11 leading-relaxed">
            Após configurar e salvar as credenciais dos seus provedores de preferência, você poderá utilizar a ação
            <strong class="text-n-brand font-semibold">"IA Gerar"</strong> no editor de funis. Em cada etapa do funil,
            você poderá definir o provedor de IA a ser utilizado, o modelo, o prompt de sistema e o prompt do usuário
            para automatizar suas interações de forma inteligente.
          </p>
        </CardLayout>
      </div>
    </div>
  </div>
</template>